// Registro central de pantallas de ec-bo (EC-3811).
//
// Es la ÚNICA fuente del nombre lógico de pantalla que sale en los eventos.
// Existe por dos razones:
//   1. Las rutas /dashboard/{support-network,my-networks,users}/:id llevan un
//      identificador de PACIENTE en el path. El nombre canónico no puede
//      contenerlo, y las tres deben colapsar a la misma pantalla porque las tres
//      renderizan el mismo componente (NetworkPortal).
//   2. Permite rankear pantallas más y menos usadas sin depender de la URL.
//
// Se usa `matchRoutes` de react-router-dom (el mismo matcher del router, con su
// ranking de especificidad y soporte de segmentos opcionales) en vez de regex
// propias, que divergirían del router en cuanto alguien agregue una ruta.
// `useMatches` NO sirve acá: requiere un data router (createBrowserRouter) y
// este repo usa <BrowserRouter> + <Routes> (src/index.js).
import { matchRoutes, type RouteObject } from "react-router-dom";

/**
 * Rutas tal como están declaradas hoy en src/App.js (nivel raíz) y en
 * src/pages/dashboard/Dashboard.tsx (todo lo de /dashboard/*).
 * Si se agrega una ruta al router y no se agrega acá, el evento sale con
 * screen: 'unknown' + path_shape → el hueco es visible en PostHog y el id NO
 * se filtra. Ver screens.test.ts.
 */
const REGISTRY = [
  // ── Públicas (src/App.js) ─────────────────────────────────────────────────
  /**
   * App.js:108 monta "/" como <Navigate to={DASHBOARD}>, así que es un rebote
   * instantáneo y no una pantalla que nadie mire. Aun así se registra: sin esta
   * entrada, entrar por el dominio pelado emitía screen: 'unknown', y ese
   * 'unknown' contamina el ranking de pantallas del dashboard de adopción
   * haciéndolo parecer un agujero de instrumentación cuando no lo es.
   *
   * Se deja emitiendo en vez de suprimirlo porque distingue "entró por el
   * dominio" de "entró por un link profundo", que es información real sobre
   * cómo llega la gente al backoffice.
   */
  { path: "/", screen: "root_redirect" },
  { path: "/login", screen: "login" },
  { path: "/sso-bridge", screen: "sso_bridge" },
  { path: "/reset-password", screen: "reset_password" },

  // ── Dashboard ─────────────────────────────────────────────────────────────
  { path: "/dashboard", screen: "dashboard_home" },
  { path: "/dashboard/team", screen: "team" },
  { path: "/dashboard/contacts", screen: "contacts" },
  { path: "/dashboard/forma", screen: "user_form" },
  { path: "/dashboard/form", screen: "all_users" },
  { path: "/dashboard/profile", screen: "my_profile" },
  { path: "/dashboard/invoices/:id?", screen: "invoices" },
  { path: "/dashboard/smartwatches", screen: "smartwatches_placeholder" },

  // ── Stock en Company Client ───────────────────────────────────────────────
  { path: "/dashboard/stock-cc", screen: "stock_cc_dashboard" },
  { path: "/dashboard/stock-cc/check-shipment", screen: "stock_cc_check_shipment" },
  { path: "/dashboard/stock-cc/await_delivery_check", screen: "stock_cc_await_delivery" },
  { path: "/dashboard/stock-cc/delivered", screen: "stock_cc_pair" },
  { path: "/dashboard/stock-cc/enrolled", screen: "stock_cc_pair_enrolled" },
  { path: "/dashboard/stock-cc/paired", screen: "stock_cc_paired" },
  { path: "/dashboard/stock-cc/logged", screen: "stock_cc_logged" },
  { path: "/dashboard/stock-cc/unpaired", screen: "stock_cc_unpaired" },
  { path: "/dashboard/stock-cc/repair", screen: "stock_cc_repair" },
  { path: "/dashboard/stock-cc/create-sw", screen: "stock_cc_create_sw" },
  { path: "/dashboard/stock-cc/load-phones", screen: "stock_cc_load_csv" },

  // ── Stock y Envíos ────────────────────────────────────────────────────────
  { path: "/dashboard/stock-shipment", screen: "stock_shipment" },

  // ── Redes de apoyo · Mis redes · Usuarios ────────────────────────────────
  // Las 3 rutas con :id renderizan NetworkPortal y colapsan a patient_profile.
  { path: "/dashboard/support-network", screen: "support_network_list" },
  { path: "/dashboard/support-network/:id", screen: "patient_profile" },
  { path: "/dashboard/my-networks", screen: "my_networks_list" },
  { path: "/dashboard/my-networks/:id", screen: "patient_profile" },
  { path: "/dashboard/users", screen: "users_list" },
  { path: "/dashboard/users/:id", screen: "patient_profile" },

  // ── Líneas telefónicas y cargas masivas ──────────────────────────────────
  { path: "/dashboard/phone-lines", screen: "phone_lines_list" },
  { path: "/dashboard/phone-lines/load-phone-lines", screen: "phone_lines_load_csv" },
  { path: "/dashboard/load-phones", screen: "smartwatch_load_csv" },

  // ── Cobertura de salud ───────────────────────────────────────────────────
  { path: "/dashboard/health-system", screen: "health_systems" },

  // ── Suscripciones y suscriptores ─────────────────────────────────────────
  { path: "/dashboard/subscriptions", screen: "subscriptions_list" },
  { path: "/dashboard/subscriptions/create", screen: "subscription_create" },
  { path: "/dashboard/subscriptions/:id", screen: "subscription_edit" },
  { path: "/dashboard/subscribers", screen: "subscribers_list" },

  // ── Company Clients ──────────────────────────────────────────────────────
  { path: "/dashboard/company_clients", screen: "company_clients_list" },
  { path: "/dashboard/company_clients/create", screen: "company_client_create" },
  { path: "/dashboard/company_clients/:id", screen: "company_client_detail" },

  // ── Consultas y reportes ─────────────────────────────────────────────────
  { path: "/dashboard/registers", screen: "registries" },
  { path: "/dashboard/event-history", screen: "alert_history" },
  { path: "/dashboard/alert-event", screen: "alerts_events" },
  { path: "/dashboard/sw-version", screen: "report_sw_version" },
  { path: "/dashboard/accounting_closing", screen: "report_accounting_closing" },
  { path: "/dashboard/whatsapp-campaing", screen: "whatsapp_campaign" },
  { path: "/dashboard/forms", screen: "jira_forms" },
] as const;

export type ScreenName = (typeof REGISTRY)[number]["screen"] | "unknown";

const ROUTES: RouteObject[] = REGISTRY.map((entry) => ({ path: entry.path }));

const SCREEN_BY_PATH = new Map<string, ScreenName>(
  REGISTRY.map((entry) => [entry.path, entry.screen as ScreenName])
);

/** Todas las pantallas conocidas. Útil para tests y para documentar la taxonomía. */
export const ALL_SCREENS: readonly ScreenName[] = Array.from(new Set(SCREEN_BY_PATH.values()));

/**
 * '/dashboard/support-network/3fa85f64-…' → 'patient_profile'
 * Nunca devuelve algo que contenga un identificador.
 */
export function resolveScreen(pathname: string): ScreenName {
  const matches = matchRoutes(ROUTES, { pathname });
  if (!matches || matches.length === 0) return "unknown";
  const matchedPath = matches[matches.length - 1].route.path;
  if (!matchedPath) return "unknown";
  return SCREEN_BY_PATH.get(matchedPath) ?? "unknown";
}

const UUID_SEGMENT = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const LONG_HEX_SEGMENT = /^[0-9a-f]{16,}$/i;
const NUMERIC_SEGMENT = /^\d+$/;

/**
 * Red de seguridad para rutas que NO estén en el registro: reemplaza cualquier
 * segmento que parezca un identificador por un placeholder. Garantiza que nunca
 * sale un UUID crudo, ni cuando alguien agrega una ruta y se olvida de registrarla.
 *
 * '/dashboard/users/3fa85f64-5717-4562-b3fc-2c963f66afa6' → '/dashboard/users/:id'
 */
export function pathShape(pathname: string): string {
  return pathname
    .split("/")
    .map((segment) => {
      if (segment === "") return segment;
      if (UUID_SEGMENT.test(segment)) return ":id";
      if (LONG_HEX_SEGMENT.test(segment)) return ":id";
      if (NUMERIC_SEGMENT.test(segment)) return ":n";
      if (segment.includes("@")) return ":redacted";
      return segment;
    })
    .join("/");
}
